import * as React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Mail, Lock, Shield } from 'lucide-react';
import apiService from '../../services/api';
import {
  signInWithFirebaseEmail,
  signInWithMicrosoft,
  extractFirebaseUserInfo,
  type FirebaseUserInfo,
} from '../../services/firebaseAuth';
import { getSamlProvider, isFirebaseInitialized } from '../../config/firebase';
import { webAppPathUrl } from '../../utils/webAppBaseUrl';
import { directionForLanguage } from '../../i18n/config';
import './Login.css';

interface LoginProps {
  onLoginSuccess: () => void;
}

function webAppLoginUrl(): string {
  return webAppPathUrl('/login');
}

export const Login: React.FC<LoginProps> = ({ onLoginSuccess }) => {
  const { t, i18n } = useTranslation('auth');
  const loginDir = directionForLanguage(i18n.language);
  const latinMarks = {
    brand: <bdi dir="ltr" />,
    ms: <bdi dir="ltr" />,
    word: <bdi dir="ltr" />,
  };
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const isFirebaseReady = isFirebaseInitialized();
  const hasMicrosoftSso = Boolean(getSamlProvider());

  const exchangeFirebaseForBackend = React.useCallback(
    async (
      userInfo: FirebaseUserInfo,
      idToken: string,
      provider: string,
      authMethod: string
    ): Promise<boolean> => {
      const token = await apiService.firebaseLogin(
        idToken,
        userInfo.email,
        userInfo.name,
        userInfo.uid,
        userInfo.emailVerified,
        provider,
        authMethod
      );
      if (token) {
        console.log('[Courtyard][AuthTrace] [Login] success, calling onLoginSuccess', {
          tokenLen: token.length,
        });
        onLoginSuccess();
        return true;
      }
      setError(t('errors.loginFailed'));
      return false;
    },
    [onLoginSuccess]
  );

  const handleMicrosoftClick = async () => {
    setError(null);
    setLoading(true);
    try {
      if (!isFirebaseReady) {
        setError(t('errors.firebaseNotConfigured'));
        return;
      }
      const result = await signInWithMicrosoft();
      if (!result.success || !result.idToken) {
        setError(result.error || t('errors.microsoftFailed'));
        return;
      }
      const userInfo = result.dialogUser
        ? result.dialogUser
        : result.user
          ? extractFirebaseUserInfo(result.user)
          : null;
      if (!userInfo) {
        setError(t('errors.microsoftFailed'));
        return;
      }
      await exchangeFirebaseForBackend(userInfo, result.idToken, 'microsoft.com', 'gcip_federation');
    } catch (err: unknown) {
      console.error('Microsoft login error:', err);
      let detail: string | undefined;
      if (typeof err === 'object' && err !== null && 'response' in err) {
        const d = (err as { response?: { data?: { detail?: unknown } } }).response?.data?.detail;
        detail = typeof d === 'string' ? d : undefined;
      }
      setError(detail ?? (err instanceof Error ? err.message : t('errors.signInRetry')));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      if (!isFirebaseReady) {
        setError(t('errors.firebaseNotConfigured'));
        return;
      }

      const firebaseResult = await signInWithFirebaseEmail(email, password);
      if (!firebaseResult.success || !firebaseResult.user || !firebaseResult.idToken) {
        setError(firebaseResult.error || t('errors.authFailed'));
        return;
      }

      await exchangeFirebaseForBackend(
        extractFirebaseUserInfo(firebaseResult.user),
        firebaseResult.idToken,
        'password',
        'firebase'
      );
    } catch (err: unknown) {
      console.error('Login error:', err);
      let detail: string | undefined;
      if (typeof err === 'object' && err !== null && 'response' in err) {
        const d = (err as { response?: { data?: { detail?: unknown } } }).response?.data?.detail;
        detail = typeof d === 'string' ? d : undefined;
      }
      setError(
        detail ?? (err instanceof Error ? err.message : t('errors.loginRetry'))
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-header">
        <div className="login-logo-wrap" dir="ltr">
          <img
            className="login-logo-img"
            src="assets/Courtyard%20Logo%20-%20tight.png"
            alt="Courtyard"
            width={280}
            height={35}
          />
        </div>
        <h1 className="login-welcome">{t('welcome')}</h1>
        <p className="login-lead">
          <span dir={loginDir}>
            <Trans t={t} i18nKey="signInSubtitle" components={latinMarks} />
          </span>
        </p>
      </div>

      <div className="login-card-wrap" dir={loginDir}>
        <div className="login-card">
          <div className="login-card-inner">
            <div className="login-sso-block">
              <div className="login-sso-label">
                <Shield className="login-sso-shield" aria-hidden />
                <span>
                  <Trans t={t} i18nKey="sso.secure" components={latinMarks} />
                </span>
              </div>
              <button
                type="button"
                className="login-ms-button"
                onClick={(ev) => {
                  ev.preventDefault();
                  ev.stopPropagation();
                  void handleMicrosoftClick();
                }}
                disabled={loading || !isFirebaseReady}
                title={!hasMicrosoftSso ? t('sso.enableHint') : undefined}
              >
                <span className="login-ms-logo" aria-hidden>
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect width="9" height="9" fill="#F25022" />
                    <rect x="11" width="9" height="9" fill="#7FBA00" />
                    <rect y="11" width="9" height="9" fill="#00A4EF" />
                    <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
                  </svg>
                </span>
                <span>
                  {loading ? t('sso.waiting') : (
                    <Trans t={t} i18nKey="sso.signInWithMicrosoft" components={latinMarks} />
                  )}
                </span>
              </button>
              <p className="login-sso-foot">
                <Trans t={t} i18nKey="sso.note" components={latinMarks} />
              </p>
            </div>

            <div className="login-divider">
              <span className="login-divider-line" />
              <span className="login-divider-text">{t('divider')}</span>
              <span className="login-divider-line" />
            </div>

            {error && <div className="login-error">{error}</div>}

            <form className="login-form" onSubmit={handleSubmit}>
              <div className="login-field">
                <label htmlFor="email" className="login-label">
                  {t('form.email')}
                </label>
                <div className="login-input-wrap">
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t('form.emailPlaceholder')}
                    required
                    disabled={loading}
                    autoComplete="email"
                    className="login-input"
                  />
                  <Mail className="login-input-icon" aria-hidden />
                </div>
              </div>

              <div className="login-field">
                <label htmlFor="password" className="login-label">
                  {t('form.password')}
                </label>
                <div className="login-input-wrap">
                  <input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t('form.passwordPlaceholder')}
                    required
                    disabled={loading}
                    autoComplete="current-password"
                    className="login-input"
                  />
                  <Lock className="login-input-icon" aria-hidden />
                </div>
              </div>

              <button type="submit" className="login-email-submit" disabled={loading}>
                {loading ? t('form.signingIn') : t('form.signInWithEmail')}
              </button>

              <div className="login-signup-row">
                <a
                  className="login-signup-link"
                  href={webAppLoginUrl()}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('form.noAccount')} <span className="login-signup-accent">{t('form.signUp')}</span>
                </a>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};
